import { token } from '@atlaskit/tokens';
import Z_INDEX from '@databases/layout/z-index';
import useContainerScrollPosition from '@databases/utils/hooks/useContainerScrollPosition';
import React, { useCallback, FunctionComponent } from 'react';
import styled, { css } from 'styled-components';

import {
  NUMBERED_COLUMN_WIDTH,
  HALF_ROW_SELECT_BUTTON_WIDTH,
} from '../../../../constants';
import { selectedBorder } from '../../styled';

import useHasNumberedEntries from './UseHasNumberedEntries';

export const StyledEntryNumberTd = styled.td<{
  hasNumberedEntries: boolean;
  isSelected: boolean;
}>`
  width: ${(props) => (props.hasNumberedEntries ? NUMBERED_COLUMN_WIDTH : 0)}px;
  left: ${HALF_ROW_SELECT_BUTTON_WIDTH}px;
  padding-top: ${token('space.150', '12px')};
  visibility: ${(props) => (props.hasNumberedEntries ? 'visible' : 'hidden')};
  justify-content: start;
  align-items: center;
  background: ${token('elevation.surface', '#FFFFF')};
  z-index: ${Z_INDEX.TABLE_NUMBERED_COLUMN};
  color: ${token('color.text.subtlest', '#6B778C')};

  // Without this entries will grow larger due to targeted css from TableLaout.
  ${({ hasNumberedEntries }) =>
    !hasNumberedEntries &&
    css`
      height: 0;
    `}

  ${({ isSelected }) =>
    isSelected &&
    css`
      background: ${token('color.background.accent.blue.subtlest')};
    `}
`;

export const EntryNumberShadow = styled.div`
  display: none;
  position: absolute;
  height: 100%;
  width: 2px;
  left: 47px;
  top: 0px;
  box-shadow: rgba(0, 0, 0, 0.1) 2px 0px 3px;
`;

const EntryNumberBorder = styled.div<{
  entryBeforeIsSelected: boolean;
  entryAfterIsSelected: boolean;
  isFirstEntry: boolean;
  isLastEntry: boolean;
}>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  border: ${selectedBorder};
  border-top: ${(props) => (props.entryBeforeIsSelected ? 'none' : selectedBorder)};
  border-bottom: ${(props) => (props.entryAfterIsSelected ? 'none' : selectedBorder)};
  margin-top: ${(props) => (props.isFirstEntry ? '0px' : '-1px')};
  margin-left: -1px;
  margin-bottom: ${(props) => (props.isLastEntry ? '0px' : '-1px')};
  margin-right: -2px;
`;

export type EntryIsSelectedMap = Record<string, boolean>;

export interface EntryNumberProps {
  entryId: string;
  isSelected: boolean;
  contentId: string;
  index: number;
  isDraggingActive: boolean;
  entryIsSelectedMap: EntryIsSelectedMap;
  isFirstEntry: boolean;
  isLastEntry: boolean;
}

const EntryNumber: FunctionComponent<EntryNumberProps> = ({
  entryId,
  isSelected,
  contentId,
  index,
  isDraggingActive,
  entryIsSelectedMap,
  isFirstEntry,
  isLastEntry,
}) => {
  const hasNumberedEntries = useHasNumberedEntries({ contentId, scope: 'main' });
  const containerScrollPosition = useContainerScrollPosition(
    'scrollable-inner-container'
  );

  const getEntrySelectionStatus = useCallback(() => {
    const entryIds = Object.keys(entryIsSelectedMap);
    const entryIndex = entryIds.indexOf(entryId);
    if (entryIndex === -1) {
      return { entryBeforeIsSelected: false, entryAfterIsSelected: false };
    }
    const entryBeforeId = entryIds[entryIndex - 1];
    const entryAfterId = entryIds[entryIndex + 1];
    return {
      entryBeforeIsSelected: entryIsSelectedMap[entryBeforeId] ?? false,
      entryAfterIsSelected: entryIsSelectedMap[entryAfterId] ?? false,
    };
  }, [entryId, entryIsSelectedMap]);

  const { entryBeforeIsSelected, entryAfterIsSelected } = getEntrySelectionStatus();

  return (
    <StyledEntryNumberTd
      className="entry-number-table-data"
      hasNumberedEntries={hasNumberedEntries}
      isSelected={isSelected && !isDraggingActive}
    >
      {isSelected && !isDraggingActive && (
        <EntryNumberBorder
          entryBeforeIsSelected={entryBeforeIsSelected}
          entryAfterIsSelected={entryAfterIsSelected}
          isFirstEntry={isFirstEntry}
          isLastEntry={isLastEntry}
        />
      )}
      {index + 1}
      <EntryNumberShadow
        style={{ display: containerScrollPosition > 0 ? 'block' : 'none' }}
      />
    </StyledEntryNumberTd>
  );
};

export default EntryNumber;
